import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getTask, cancelTask } from '@/api/project';
import { 
  ArrowLeft, 
  RefreshCw, 
  StopCircle, 
  Copy, 
  Check, 
  ExternalLink,
  Pause,
  Play,
  ArrowDown,
  Braces
} from 'lucide-react';

// Try to format metadata as pretty JSON
function formatMetadata(metadata: string): string {
  try {
    // Handle Python-style single quotes and values
    let cleaned = metadata
      .replace(/'/g, '"')
      .replace(/None/g, 'null')
      .replace(/True/g, 'true')
      .replace(/False/g, 'false');
    const parsed = JSON.parse(cleaned);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // If parsing fails, return original
    return metadata;
  }
}

// Metadata card component with format toggle
function MetadataCard({ 
  metadata, 
  onCopy, 
  copied 
}: { 
  metadata: string; 
  onCopy: (text: string) => void; 
  copied: boolean;
}) {
  const [isFormatted, setIsFormatted] = useState(true);
  const displayContent = isFormatted ? formatMetadata(metadata) : metadata;
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span>Additional Information</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <pre className="bg-gray-100 p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto">
          {displayContent}
        </pre>
        <div className="flex gap-2 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFormatted(!isFormatted)}
          >
            <Braces className="h-4 w-4 mr-1" />
            {isFormatted ? 'Show Raw' : 'Format JSON'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopy(displayContent)}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 0) return '-';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function getStatusBadge(status: string) {
  const statusClasses: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-800',
    running: 'bg-blue-100 text-blue-800 animate-pulse',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <Badge className={statusClasses[status] || 'bg-gray-100 text-gray-800'}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

// Convert ANSI color codes to HTML spans and format output
function ansiToHtml(text: string): string {
  if (!text) return '';
  
  // First, convert literal \n to actual newlines (common in JSON-escaped output)
  let processed = text.replace(/\\n/g, '\n');
  
  // Also handle \t for tabs
  processed = processed.replace(/\\t/g, '  ');
  
  const ansiColorMap: Record<string, string> = {
    '30': 'color: #333',
    '31': 'color: #e74c3c',
    '32': 'color: #2ecc71',
    '33': 'color: #f1c40f',
    '34': 'color: #3498db',
    '35': 'color: #9b59b6',
    '36': 'color: #1abc9c',
    '37': 'color: #ecf0f1',
    '90': 'color: #7f8c8d',
    '91': 'color: #e74c3c',
    '92': 'color: #2ecc71',
    '93': 'color: #f1c40f',
    '94': 'color: #3498db',
    '95': 'color: #9b59b6',
    '96': 'color: #1abc9c',
    '97': 'color: #fff',
  };
  
  // Escape HTML
  let escaped = processed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Handle ANSI escape sequences
  const ansiRegex = /\u001b\[(\d+)m/g;
  let currentStyle = '';
  let result = '';
  let lastIndex = 0;
  let match;
  
  while ((match = ansiRegex.exec(escaped)) !== null) {
    const textBefore = escaped.substring(lastIndex, match.index);
    if (currentStyle) {
      result += `<span style="${currentStyle}">${textBefore}</span>`;
    } else {
      result += textBefore;
    }
    
    const code = match[1];
    if (code === '0') {
      currentStyle = '';
    } else if (ansiColorMap[code]) {
      currentStyle = ansiColorMap[code];
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  const remainingText = escaped.substring(lastIndex);
  if (currentStyle) {
    result += `<span style="${currentStyle}">${remainingText}</span>`;
  } else {
    result += remainingText;
  }
  
  return result;
}

export function TaskDetailPage() {
  const { projectId, taskId } = useParams<{ projectId: string; taskId: string }>();
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['task', projectId, taskId],
    queryFn: () => getTask(projectId!, parseInt(taskId!)),
    enabled: !!projectId && !!taskId,
    refetchInterval: autoRefresh && !isPaused ? 5000 : false,
  });

  const task = data?.task;

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => cancelTask(projectId!, parseInt(taskId!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', projectId, taskId] });
    },
  });

  // Auto-enable refresh for running tasks
  useEffect(() => {
    if (task?.status === 'running' || task?.status === 'pending') {
      setAutoRefresh(true);
    } else {
      setAutoRefresh(false);
    }
  }, [task?.status]);

  // Scroll to bottom when logs update
  useEffect(() => {
    if (!isPaused && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [task?.output_log, isPaused]);

  // Handle scroll position for scroll-to-bottom button
  const handleScroll = useCallback(() => {
    if (logContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 50;
      setShowScrollButton(!isAtBottom);
    }
  }, []);

  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !task) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">Failed to load task details</p>
          <Link to={`/project/${projectId}/tasks`}>
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tasks
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const canCancel = task.status === 'running' || task.status === 'pending';

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-sm text-gray-500 mb-1">
            <Link to={`/project/${projectId}/tasks`} className="hover:text-primary">
              Tasks
            </Link>
          </div>
          <h1 className="text-2xl font-bold">
            Task #{task.id}: {task.title}
          </h1>
          <p className="text-gray-600 mt-1">
            {task.description || 'No description provided'}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant={autoRefresh ? 'default' : 'secondary'}>
              Auto Refresh: {autoRefresh ? 'ON' : 'OFF'}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canCancel && (
            <Button
              variant="destructive"
              onClick={() => {
                if (confirm('Are you sure you want to cancel this task?')) {
                  cancelMutation.mutate();
                }
              }}
              disabled={cancelMutation.isPending}
            >
              <StopCircle className="h-4 w-4 mr-2" />
              Cancel Task
            </Button>
          )}
          <Button
            variant={autoRefresh ? 'destructive' : 'outline'}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Disable Auto Refresh' : 'Enable Auto Refresh'}
          </Button>
          <Link to={`/project/${projectId}/tasks`}>
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tasks
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task Details - Left Column */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Task Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-500">Status</span>
                <span>{getStatusBadge(task.status)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-500">Type</span>
                <span className="font-medium">
                  {task.task_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-500">Initiated By</span>
                <span className="font-medium">{task.created_by || '-'}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-500">Created</span>
                <span>{formatDateTime(task.created_at)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-500">Started</span>
                <span>{formatDateTime(task.started_at)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-500">Completed</span>
                <span>{formatDateTime(task.completed_at)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm text-gray-500">Duration</span>
                <span className="font-medium">{formatDuration(task.duration)}</span>
              </div>
              {task.build_name && (
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-500">Related Deployment</span>
                  <Link
                    to={`/project/${projectId}/deployments`}
                    className="text-primary hover:underline"
                  >
                    {task.build_name}
                  </Link>
                </div>
              )}
              {task.command && (
                <div className="pt-2">
                  <div className="text-sm text-gray-500 mb-1">Command</div>
                  <code className="block bg-gray-100 p-2 rounded text-xs break-all">
                    {task.command}
                  </code>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          {task.task_metadata && (
            <MetadataCard 
              metadata={task.task_metadata} 
              onCopy={(text) => copyToClipboard(text, 'metadata')}
              copied={copied === 'metadata'}
            />
          )}
        </div>

        {/* Task Logs - Right Column (2 cols) */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle>Task Output</CardTitle>
              <div className="flex items-center gap-2">
                {isPaused ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsPaused(false);
                      refetch();
                    }}
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Resume
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsPaused(true)}
                  >
                    <Pause className="h-4 w-4 mr-1" />
                    Pause
                  </Button>
                )}
                {showScrollButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={scrollToBottom}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(task.output_log || '', 'output')}
                >
                  {copied === 'output' ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a
                    href={`/api/project/${projectId}/tasks/${taskId}?format=text`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Raw
                  </a>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div
                ref={logContainerRef}
                onScroll={handleScroll}
                className="bg-gray-100 text-gray-800 p-4 font-mono text-[11px] leading-relaxed h-[700px] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border"
                dangerouslySetInnerHTML={{ __html: ansiToHtml(task.output_log || 'No output yet...') }}
              />
            </CardContent>
          </Card>

          {/* Error Log */}
          {task.error_log && (
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-red-600">Error Log</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(task.error_log!, 'error')}
                >
                  {copied === 'error' ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </>
                  )}
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                <div
                  className="bg-red-50 text-red-800 p-4 font-mono text-[11px] leading-relaxed h-[200px] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-red-200"
                  dangerouslySetInnerHTML={{ __html: ansiToHtml(task.error_log) }}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
